import {Filter, Interweave} from "interweave";
import ItemAttribute from "./ItemAttribute";
import React from "react";
import AttributeDefinition from "../../domain/AttributeDefinition";
import AttributeBox from "./AttributeBox";
import {VulSourceType} from "../../domain/VulSourceType";
import {HashtagMatcher} from "interweave-autolink";
import * as styles from "./VulSourceItemDescription.module.css";
import MoreLess from "../common/MoreLess";
import MarkdownContent from "../common/MarkdownContent";
import {Descriptions} from "antd";
import {canRenderAttributeValue} from "./ItemAttributeRenderUtils";
import 'property-information';
import ExpandableItemList from "../common/ExpandableItemList";

const MAX_INLINE_AFFECTED_VERSIONS = 5;

class ImgFilter extends Filter {
    // @ts-ignore
    attribute(name: string, value: string): string {
        return value;
    }

    node(name: string, node: HTMLElement): HTMLElement {
        if (name === 'img') {
            return null;
        }

        return node;
    }
}

class WidthFilter extends Filter {
    // @ts-ignore
    attribute(name: string, value: string): string {
        if (name === "width") {
            return null;
        }

        return value;
    }

    node(name: string, node: HTMLElement): HTMLElement {
        return node;
    }
}

interface Props {
    sourceType: VulSourceType;
    attributes?: any;
    affected?: any;
    attributeDefinitions?: AttributeDefinition[];
    showDescription?: boolean;
    showAttributes?: boolean;
    showAffected?: boolean;
    showTitle?: boolean;
    hideSealScoreTag?: boolean;
    hideLink?: boolean;
    attributeLayout?: "horizontal" | "vertical";
    framed?: boolean;
}

function VulSourceItemDescription(props: Props) {
    const attributes = props.attributes;

    let interweaveProps = {};

    switch (props.sourceType) {
        case VulSourceType.TWITTER:
            interweaveProps = {
                ...interweaveProps,
                matchers: [new HashtagMatcher('hashtag')],
                hashtagUrl: "https://twitter.com/hashtag/{{hashtag}}"
            }
            break;
        case VulSourceType.RSS:
            interweaveProps = {
                ...interweaveProps,
                filters: [new ImgFilter(), new WidthFilter()]
            }
            break;
    }

    const definitions: any = {};

    props.attributeDefinitions?.forEach(definition => definitions[definition.name] = definition);

    const descriptionNode = renderDescription();
    const affectedNode = (props.showAffected === undefined || props.showAffected === true) ? renderAffected() : null;
    const attributesNode = renderAttributes();

    if (!descriptionNode && !affectedNode && !attributesNode) {
        return null;
    }

    const inner = (
        <div className={styles.content}>
            {descriptionNode}
            {affectedNode}
            {attributesNode}
        </div>
    );

    if (props.framed) {
        return (
            <div className={styles['content-framed']}>
                <div className={styles['content-framed-header']}>Source data</div>
                {inner}
            </div>
        );
    }

    return inner;

    function renderDescription() {
        if (props.showDescription !== undefined && props.showDescription !== true) {
            return null;
        }

        const showTitle = props.showTitle === undefined || props.showTitle === true;
        const hasTitle = showTitle && !!attributes?.title;
        const hasDescription = !!attributes?.description;
        const renderMarkdown = props.sourceType === VulSourceType.OSV
                || props.sourceType === VulSourceType.BROADCOM_SECURITY_ADVISORY;

        if (!hasTitle && !hasDescription) {
            return <div><i>No description available</i></div>;
        }

        return <>
            {hasTitle && hasDescription && <div><b>{attributes.title}</b></div>}
            {hasTitle && !hasDescription && <div>{attributes.title}</div>}
            {hasDescription && (
                <MoreLess textMore={"« show full description »"} textLess={"» hide full description «"}>
                    {!renderMarkdown && <div><Interweave content={attributes.description} {...interweaveProps} /></div>}
                    {renderMarkdown && <MarkdownContent children={attributes.description}/>}
                </MoreLess>
            )}
        </>
    }

    function renderAttributes() {
        if (props.showAttributes !== undefined && props.showAttributes !== true) {
            return null;
        }

        const showLink = !props.hideLink && !!attributes?.link;
        const visibleAttributeKeys = Object.keys(props.attributes ?? {})
            .filter(key => !props.hideSealScoreTag || key !== 'sealScore')
            .filter(key => canRenderAttributeValue(key, attributes[key], definitions[key]));

        if (!showLink && visibleAttributeKeys.length === 0) {
            return null;
        }

        return (
            <div className={`${styles.attributes} ${(props.attributeLayout === 'vertical') ? styles['attributes-vertical'] : ''}`}>
                {showLink && <AttributeBox value={"External link"} href={attributes.link} showSearch={false}/>}
                {visibleAttributeKeys.map(key => (
                    <ItemAttribute key={key} definition={definitions[key]} value={attributes[key]}/>
                ))}
            </div>
        );
    }

    function renderAffected() {
        if (props.affected === undefined || props.affected === null) {
            return null;
        }

        if (Array.isArray(props.affected) && props.affected.length === 0) {
            return null;
        }

        return <>
            <b>Affects:</b>
            <MoreLess textMore={"« show full affected versions »"} textLess={"» hide full affected versions «"}>
                <Descriptions column={1} className={styles['affected-packages']} bordered={false}>
                    {props.affected.map(item => <Descriptions.Item key={item?.package?.purl} label={<span title={item?.package?.purl}>{item?.package?.name}{item?.package?.ecosystem ? ` (${item?.package?.ecosystem})` : ''}</span>}>
                        {createAffectsVersionText(item)}
                    </Descriptions.Item>)}
                </Descriptions>
            </MoreLess>
        </>
    }

    function createAffectsVersionText(affectedPackage: any) {
        const ranges = affectedPackage.ranges
            ?.map(createAffectsRangeText)
            .filter(Boolean);

        if (ranges?.length > 0) {
            return <>{ranges.join(", ")}</>;
        }

        return renderAffectedVersions(affectedPackage.versions);
    }

    function createAffectsRangeText(range: any) {
        let tokens = [];

        for (const event of range.events ?? []) {
            const introduced = event['introduced'];
            const introducedZero = introduced !== undefined && introduced === "0";
            const fixed = event['fixed'];
            const lastAffected = event['last_affected'];

            if (introduced != undefined) {
                if (!introducedZero) {
                    tokens.push(`>= ${introduced}`);
                }
            }

            if (fixed !== undefined) {
                tokens.push(`< ${fixed}`);
            }

            if (lastAffected !== undefined) {
                if (tokens.length !== 0 && tokens[tokens.length - 1] === `>= ${lastAffected}`) {
                    tokens.pop();

                    tokens.push(`${lastAffected}`);
                } else {
                    tokens.push(`<= ${lastAffected}`);
                }
            }
        }

        return tokens.join(", ");
    }

    function renderAffectedVersions(versions?: string[]) {
        if (!versions || versions.length === 0) {
            return <i>affected</i>;
        }

        return (
            <span className={styles['affected-version-list']}>
                <ExpandableItemList
                    items={[...versions].sort()}
                    maxItems={MAX_INLINE_AFFECTED_VERSIONS}
                    renderItem={version => version}
                    separator={", "}
                    toggleSeparator={" "}
                    buttonClassName={styles['affected-version-toggle']}
                    renderExpandLabel={({hiddenCount}) => <i>+{hiddenCount} more</i>}
                    renderCollapseLabel={() => <i>show fewer</i>}
                />
            </span>
        );
    }
}

export default VulSourceItemDescription;
